import { Fragment, useEffect, useMemo, useState } from "react";
import { KeetaSuggestButton } from "./KeetaSuggestDialog";
import { toast } from "sonner";
import { ChevronDown, Link2, Loader2, MinusCircle, PlusCircle, Receipt, Search, Users } from "lucide-react";
import { api } from "../lib/api";

const f = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

const SummaryLines = ({ summary }) => {
  const lines = (summary?.lines || []).filter((l) => l.amount);
  if (!lines.length) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="keeta-summary-lines">
      <p className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-700"><Receipt className="h-4 w-4" /> ملخص الفاتورة</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {lines.map((l) => (
          <div key={l.label} className="rounded-xl bg-slate-50 px-3 py-2">
            <p className="truncate text-[11px] text-slate-500" title={l.label}>{l.label}</p>
            <p className={`font-mono-nums text-sm font-bold ${l.amount < 0 ? "text-rose-700" : "text-slate-800"}`}>{f(l.amount)}</p>
          </div>
        ))}
        <div className="rounded-xl bg-slate-100 px-3 py-2"><p className="text-[11px] text-slate-500">قبل الضريبة</p><p className="font-mono-nums text-sm font-bold">{f(summary.amount_excl_vat)}</p></div>
        <div className="rounded-xl bg-slate-100 px-3 py-2"><p className="text-[11px] text-slate-500">الضريبة</p><p className="font-mono-nums text-sm font-bold">{f(summary.vat)}</p></div>
        <div className="rounded-xl bg-indigo-600 px-3 py-2 text-white" data-testid="keeta-summary-total"><p className="text-[11px] opacity-80">إجمالي الفاتورة</p><p className="font-mono-nums text-sm font-bold">{f(summary.amount_incl_vat)}</p></div>
      </div>
    </div>
  );
};

const Chips = ({ items, tone, title, Icon, testid }) => (
  <div className={`rounded-2xl border p-4 ${tone === "rose" ? "border-rose-200 bg-rose-50/50" : "border-emerald-200 bg-emerald-50/50"}`} data-testid={testid}>
    <p className={`mb-2 flex items-center gap-2 text-sm font-bold ${tone === "rose" ? "text-rose-800" : "text-emerald-800"}`}><Icon className="h-4 w-4" /> {title}</p>
    {!items?.length ? <p className="text-xs text-slate-500">لا يوجد</p> : (
      <div className="flex flex-wrap gap-2">
        {items.map((x) => (
          <span key={x.label} className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs">
            <b className={tone === "rose" ? "text-rose-700" : "text-emerald-700"}>{x.label}</b> · {x.count} مرة · <span className="font-mono-nums font-bold">{f(x.amount)}</span> ر.س
          </span>
        ))}
      </div>
    )}
  </div>
);

const RiderDetail = ({ r, det, lines, cols }) => (
  <tr className="bg-slate-50/70"><td colSpan={cols} className="space-y-2 p-3" data-testid={`keeta-rider-detail-${r.rider_id}`}>
    {r.reason && <p className="text-[11px] font-semibold text-rose-700">سبب عدم الصلاحية: {r.reason}</p>}
    <div className="flex flex-wrap gap-1.5">
      {Object.entries(lines || {}).map(([k, v]) => <span key={k} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px]">{k} {v.count} × · {f(v.amount)} ر.س</span>)}
      {(r.components || []).map((c) => <span key={c.label} className={`rounded-lg border bg-white px-2 py-1 text-[11px] ${c.amount < 0 ? "border-rose-100 text-rose-700" : "border-emerald-100 text-emerald-700"}`}>{c.label}: {f(c.amount)}</span>)}
    </div>
    {det.length > 0 && <div className="flex flex-wrap gap-1.5">{det.map((x) => <span key={x.kind + x.label} className={`rounded-lg px-2 py-1 text-[11px] ${x.kind === "ded" ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"}`}>{x.kind === "ded" ? "−" : "+"} {x.label}: {x.count} × · {f(x.amount)} ر.س</span>)}</div>}
  </td></tr>
);

export const KeetaInvoiceView = ({ invoiceId, canEdit, summary }) => {
  const [d, setD] = useState(null);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState("");
  const [q, setQ] = useState("");
  const [vf, setVf] = useState("all");
  const [busy, setBusy] = useState(false);
  const load = () => api.keetaInvoice(invoiceId).then(setD).catch(() => toast.error("تعذّر تحليل فاتورة كيتا"));
  useEffect(() => { setD(null); load(); }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps
  const autolink = async () => {
    setBusy(true);
    try { const r = await api.keetaAutolink(invoiceId); toast.success(`تم ربط ${r.linked} مندوب (${r.by_name} بالاسم) · بدون ربط ${r.unlinked}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الربط التلقائي"); } finally { setBusy(false); }
  };
  useEffect(() => { if (canEdit) api.listEmployees().then((l) => setEmps(Array.isArray(l) ? l : l.items || [])).catch(() => {}); }, [canEdit]);
  const rows = useMemo(() => (d?.riders || []).filter((r) => (vf === "all" || (vf === "valid" ? r.valid === true : r.valid === false))
    && (!q || `${r.name} ${r.rider_id} ${r.employee_name || ""}`.toLowerCase().includes(q.toLowerCase()))), [d, q, vf]);
  const link = async (r, employee_id) => {
    try {
      await api.keetaLink(r.rider_id, employee_id);
      const e = emps.find((x) => x.id === employee_id);
      setD((p) => ({ ...p, riders: p.riders.map((x) => (x.rider_id === r.rider_id ? { ...x, employee_id: employee_id || null, employee_name: e?.name || null, match: employee_id ? "manual" : "" } : x)) }));
      toast.success(employee_id ? "تم الربط" : "تم إلغاء الربط");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الربط"); }
  };
  if (!d) return <div className="py-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div>;
  const T = d.tiers;
  const tot = rows.reduce((a, r) => ({ orders: a.orders + r.orders, fees: a.fees + r.fees, support: a.support + r.support, bonuses: a.bonuses + r.bonuses, deductions: a.deductions + r.deductions, net: a.net + r.net, distance: a.distance + (r.distance || 0) }), { orders: 0, fees: 0, support: 0, bonuses: 0, deductions: 0, net: 0, distance: 0 });
  const nValid = d.riders.filter((r) => r.valid === true).length, nInvalid = d.riders.filter((r) => r.valid === false).length;
  const heads = ["المندوب (كيتا)", "الموظف", ...(T ? ["الحالة", "أيام", "ساعات/يوم", "المسافة كم"] : []), "الطلبات", T ? "التسعير حسب الطلب" : "رسوم التوصيل", T ? "رسوم المسافة" : "الدعم", "مكافآت", "الخصومات", "الصافي", ""];
  return (
    <div className="space-y-4" data-testid="keeta-invoice-view">
      {T && <SummaryLines summary={summary} />}
      <div className="grid gap-3 lg:grid-cols-2">
        {T && <Chips items={d.incentives} tone="emerald" title="الحوافز والتعديلات (من ورقة الطلبات)" Icon={PlusCircle} testid="keeta-incentives" />}
        <Chips items={d.deductions} tone="rose" title="تفصيل الخصومات (من ورقة الطلبات)" Icon={MinusCircle} testid="keeta-deductions" />
      </div>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b bg-slate-50 px-4 py-2">
          <p className="flex items-center gap-2 text-sm font-bold text-slate-700"><Users className="h-4 w-4" /> المندوبون ({d.riders.length})</p>
          {T && [["all", `الكل`], ["valid", `صالح ${nValid}`], ["invalid", `غير صالح ${nInvalid}`]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setVf(k)} data-testid={`keeta-valid-filter-${k}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${vf === k ? "bg-slate-800 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{l}</button>
          ))}
          {canEdit && <button type="button" onClick={autolink} disabled={busy} data-testid="keeta-autolink-btn"
            className="flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} ربط تلقائي ({d.riders.filter((r) => !r.employee_id).length} بدون ربط)</button>}
          {canEdit && d.riders.some((r) => !r.employee_id) && <KeetaSuggestButton invId={invoiceId} onDone={load} />}
          <div className="relative mr-auto"><Search className="absolute right-2 top-2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم أو المعرّف" data-testid="keeta-rider-search" className="h-8 w-52 rounded-lg border border-slate-200 pr-8 text-xs" /></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-right text-xs">
            <thead className="text-slate-500"><tr>{heads.map((h, i) => <th key={i} className="whitespace-nowrap p-2">{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r) => {
                const det = d.per_rider[r.rider_id] || [];
                const hasDet = det.length || (r.components || []).length || d.order_lines?.[r.rider_id] || r.reason;
                return (
                  <Fragment key={r.rider_id}>
                    <tr className={`border-t ${r.employee_id ? "" : "bg-amber-50/60"}`} data-testid={`keeta-rider-${r.rider_id}`}>
                      <td className="p-2"><b>{r.name}</b><div className="font-mono text-[10px] text-slate-400">{r.rider_id}</div></td>
                      <td className="p-2">
                        {canEdit ? (
                          <select value={r.employee_id || ""} onChange={(e) => link(r, e.target.value)} data-testid={`keeta-link-${r.rider_id}`}
                            className={`h-8 w-44 rounded-md border bg-white px-1 text-xs ${r.employee_id ? "border-slate-200" : "border-amber-400"}`}>
                            <option value="">— اربط بموظف —</option>
                            {emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                          </select>
                        ) : (r.employee_name || "—")}
                        {r.match === "name" && <span className="mt-0.5 block text-[10px] font-semibold text-indigo-600" data-testid={`keeta-match-name-${r.rider_id}`}>رُبط تلقائياً بالاسم — تحقّق</span>}
                        {r.match === "id" && <span className="mt-0.5 block text-[10px] text-slate-400">بمعرّف كيتا</span>}
                      </td>
                      {T && <>
                        <td className="p-2" title={r.reason}>{r.valid == null ? "—" : <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold ${r.valid ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`} data-testid={`keeta-valid-${r.rider_id}`}>{r.valid ? "صالح" : "غير صالح"}</span>}</td>
                        <td className="p-2 font-mono-nums">{r.days || "—"}</td><td className="p-2 font-mono-nums">{r.hours || "—"}</td><td className="p-2 font-mono-nums">{f(r.distance)}</td>
                      </>}
                      <td className="p-2 font-mono-nums">{r.orders}</td>
                      <td className="p-2 font-mono-nums">{f(r.fees)}</td>
                      <td className="p-2 font-mono-nums">{f(r.support)}</td>
                      <td className="p-2 font-mono-nums text-emerald-700">{f(r.bonuses)}</td>
                      <td className="p-2 font-mono-nums text-rose-700">{f(r.deductions)}</td>
                      <td className="p-2 font-mono-nums font-bold">{f(r.net)}</td>
                      <td className="p-2">{hasDet && (
                        <button type="button" onClick={() => setOpen(open === r.rider_id ? "" : r.rider_id)} data-testid={`keeta-rider-ded-${r.rider_id}`} className="rounded p-1 text-slate-600 hover:bg-slate-100">
                          <ChevronDown className={`h-4 w-4 transition-transform ${open === r.rider_id ? "rotate-180" : ""}`} />
                        </button>)}
                      </td>
                    </tr>
                    {open === r.rider_id && <RiderDetail r={r} det={det} lines={d.order_lines?.[r.rider_id]} cols={heads.length} />}
                  </Fragment>
                );
              })}
              <tr className="border-t bg-indigo-50 font-bold text-indigo-900" data-testid="keeta-riders-total">
                <td className="p-2" colSpan={T ? 5 : 2}>الإجمالي</td>
                {T && <td className="p-2 font-mono-nums">{f(tot.distance)}</td>}
                <td className="p-2 font-mono-nums">{tot.orders}</td><td className="p-2 font-mono-nums">{f(tot.fees)}</td><td className="p-2 font-mono-nums">{f(tot.support)}</td>
                <td className="p-2 font-mono-nums">{f(tot.bonuses)}</td><td className="p-2 font-mono-nums">{f(tot.deductions)}</td><td className="p-2 font-mono-nums">{f(tot.net)}</td><td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
